import _ from 'lodash'
import { formatPhone, isPhone } from '../utils/phone'
import PresenceStatus from './enums/PresenceStatus'
import { cloudFrontUrlTransform } from './extensions/cloudFrontTransform'
import addEnsureEntity from './extensions/ensureEntity'

export const PROFILE_ATTRIBUTES_PER_ORG = ['autoForwardReceiverIds', 'conversationId', 'department', 'title']

export default function(store, { host }) {
  const User = store.defineModel('user', {
    replaceKeys: {
      avatar: 'avatarUrl',
      blockedPersonal: 'contactsMessagingBlockedByOrganizationIds',
      organizationKey: 'organizationId',
      organizationToken: 'organizationId',
      timeToLive: 'messagesTTL'
    },

    defaultValues: {
      botRole: null,
      contactsMessagingBlockedByOrganizationIds: [],
      isPatient: false,
      isPatientContact: false,
      organizationIds: [],
      presence: PresenceStatus.UNKNOWN,
      profileByOrganizationId: {},
      roles: [],
    },

    skipCamelizationForKeys: [
      'metadata',
    ],

    parseAttrs(attrs) {
      const organizationId = attrs.organizationId

      if (attrs.dndAutoForwardReceivers) {
        attrs.autoForwardReceiverIds = attrs.dndAutoForwardReceivers.map(({ token }) => token)
      } else if (attrs.dndAutoForwardReceiver === '' || attrs.dndAutoForwardReceiver === 'undefined') {
        attrs.autoForwardReceiverIds = []
      } else if (attrs.dndAutoForwardReceiver !== undefined) {
        attrs.autoForwardReceiverIds = [attrs.dndAutoForwardReceiver]
      }
      delete attrs.dndAutoForwardReceiver
      delete attrs.dndAutoForwardReceivers

      if (organizationId) {
        delete attrs.organizationId
        if (!attrs.organizationIds) attrs.organizationIds = []
        if (!attrs.organizationIds.includes(organizationId)) {
          attrs.organizationIds.push(organizationId)
        }
        const profileAttrs = { ..._.pick(attrs, PROFILE_ATTRIBUTES_PER_ORG), userId: attrs.id, organizationId }
        delete attrs.autoForwardReceiverIds
        delete attrs.conversationId

        if (!attrs.profileByOrganizationId) attrs.profileByOrganizationId = {}
        attrs.profileByOrganizationId[organizationId] = profileAttrs
      }

      if ('metadata' in attrs) {
        if (
        'feature_service' in attrs.metadata &&
        attrs.metadata.feature_service === 'patient_messaging'
        ) {
          const patientData = attrs.metadata
          const { is_patient_contact: isPatientContact } = patientData

          // TODO: Remove the unnecessary check once server returns a true boolean in search. Response in roster is fine
          if (
            (typeof isPatientContact === 'string' && isPatientContact === 'true') ||
            (typeof isPatientContact === 'boolean' && isPatientContact)
          ) {
            attrs.patientContactId = `patientContact:${attrs.id}`
            attrs.isPatientContact = true
          } else {
            attrs.patientId = `patient:${attrs.id}`
            attrs.isPatient = true
            host.users._injectPatientEntity(patientData, attrs.id)
          }
        }
        delete attrs['metadata']
      }

      if (attrs.avatarUrl) {
        attrs.avatarUrl = cloudFrontUrlTransform(attrs.avatarUrl, host.config)
      }

      if (!attrs.displayName && attrs.name) {
        attrs.displayName = attrs.name
      }

      if (attrs.displayName && isPhone(attrs.displayName)) {
        attrs.displayName = formatPhone(attrs.displayName)
      }

      return attrs
    },

    beforeInject(attrs, existingEntity) {
      if (existingEntity) {
        if (attrs.organizationIds) {
          attrs.organizationIds = _.union(
            existingEntity.organizationIds,
            attrs.organizationIds
          )
        }
        if (attrs.profileByOrganizationId) {
          const newProfiles = {}
          if (existingEntity.profileByOrganizationId) {
            for (const [key, entry] of Object.entries(existingEntity.profileByOrganizationId)) {
              newProfiles[key] = {}
              Object.assign(newProfiles[key], entry)
            }
          }
          for (const [key, entry] of Object.entries(attrs.profileByOrganizationId)) {
            if (!newProfiles[key]) newProfiles[key] = {}
            Object.assign(newProfiles[key], entry)
          }
          attrs.profileByOrganizationId = newProfiles
        }
      }
    },

    afterAssignment(entity) {
      const { profileByOrganizationId } = entity

      entity.contactsMessagingBlocked = entity.contactsMessagingBlockedByOrganizationIds.length > 0
      entity.isRoleBot = entity.metadata.some(({ data }) => data['feature_service'] === 'role')
      entity.shouldDisplay = !entity.metadata.some(({ data }) => data['invisible'] === 'true')

      for (const profile of Object.values(profileByOrganizationId)) {
        if (!entity.dnd) profile.autoForwardReceiverIds = []
        host.users._setMembersOnEntity(profile, 'autoForwardReceiverIds', 'autoForwardReceivers')
      }
    },

    relations: {
      belongsTo: {
        patient: { type: 'patient', foreignKey: 'patientId' },
        patientContact: { type: 'patientContact', foreignKey: 'patientContactId' },
      },
      hasMany: {
        metadata: {
          type: 'metadata', foreignKey: 'entityId',
        },
      },
    },
  })

  addEnsureEntity(User, {
    finder: (...args) => host.users.find(...args),
    placeholderEntityDelayBeforeRefreshing() { return host.config.placeholderEntityDelayBeforeRefreshing },
    placeholderEntityAllowLoading() { return host.config.placeholderEntityAllowLoading },

    defaultPlaceholderAttrs: { displayName: 'Loading...' },
    defaultNotFoundAttrs: { displayName: '<User Not Found>' },
  })

  return User
}
